import * as React from "react";
import { useFlowFSList } from "../hooks";
import { useFlowFSStore } from "../state/flowfs-store";
import { useShallow } from "zustand/react/shallow";
import { Button } from "@/components/ui/button";

export default function FlowFSList({
  tenantId,
  flowId,
  prefix,
  recursive,
  limit,
  cursor,
  onCursorChange,
}: {
  tenantId: string;
  flowId: string;
  prefix: string;
  recursive: boolean;
  limit: number;
  cursor?: string;
  onCursorChange: (c?: string) => void;
}) {
  const { data, isLoading, isError, error } = useFlowFSList(tenantId, flowId, { prefix, recursive, limit, cursor });
  const { select, canEnter, markDenied } = useFlowFSStore(
    useShallow((s) => ({ select: s.select, canEnter: s.canEnter, markDenied: s.markDenied }))
  );

  React.useEffect(() => {
    const err = error as any;
    if (err?.status === 403) {
      const { denyPrefixes } = useFlowFSStore.getState();
      if (!denyPrefixes.has(prefix)) markDenied(prefix);
    }
  }, [error, markDenied, prefix]);

  if (isLoading) return <div className="p-3 text-sm">Loading…</div>;
  if (isError)
    return <div className="p-3 text-red-600 text-sm">{(error as any)?.message ?? "Failed to load"}</div>;

  const items = data?.items ?? [];
  return (
    <div className="flex-1 overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-muted/50">
          <tr className="text-left">
            <th className="px-2 py-1">Name</th>
            <th className="px-2 py-1">Type</th>
            <th className="px-2 py-1">Size</th>
            <th className="px-2 py-1">Modified</th>
            <th className="px-2 py-1">Path</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr
              key={it.path}
              className="hover:bg-muted cursor-pointer"
              onClick={() => {
                if (it.type === "dir") {
                  if (canEnter(it.path)) {
                    useFlowFSStore
                      .getState()
                      .setPrefix(it.path.endsWith("/") ? it.path : it.path + "/");
                  }
                } else {
                  select({ path: it.path, type: "file" });
                }
              }}
            >
              <td className="px-2 py-1">{it.name}</td>
              <td className="px-2 py-1">{it.type}</td>
              <td className="px-2 py-1 tabular-nums">{it.size ?? ""}</td>
              <td className="px-2 py-1">{it.modified ? new Date(it.modified).toLocaleString() : ""}</td>
              <td className="px-2 py-1 font-mono text-xs">{it.path}</td>
            </tr>
          ))}
          {!items.length && (
            <tr>
              <td colSpan={5} className="px-2 py-6 text-muted-foreground text-center">
                No items
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="border-t p-2 flex items-center gap-2 justify-end">
        <Button variant="outline" disabled={!cursor} onClick={() => onCursorChange(undefined)}>
          Reset
        </Button>
        <Button variant="outline" disabled={!data?.cursor} onClick={() => onCursorChange(data?.cursor)}>
          Next
        </Button>
      </div>
    </div>
  );
}
